import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { postLogin } from "@client/api/endpoints";
import { useMutation } from "@client/hooks/useMutation";
import { t } from "@client/lib/dictionary";
import { loginSchema, type LoginInput } from "@client/lib/schemas/auth";
import { useLayoutStore } from "@client/store/layout";
import { useMetricMetaStore } from "@client/store/metricMeta";
import { useUserStore } from "@client/store/user";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const LoginForm: React.FC = () => {
  const nav = useNavigate();
  const setUser = useUserStore((s) => s.setUser);
  const fetchFoscams = useUserStore((s) => s.fetchFoscams);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "" },
  });

  const { mutate } = useMutation(postLogin);

  const onSubmit = async (data: LoginInput) => {
    try {
      const res = await mutate(data);
      if (res.success && res.responseObject) {
        toast.success(t.auth.loginSuccess);
        setUser(res.responseObject as unknown as UserSafe);
        // Foscams + topic metadata se v `fetchInitial` načítají jen na refresh — po loginu je dotáhneme sem.
        void fetchFoscams();
        void useMetricMetaStore.getState().load();
        // Layouty taky. Bootují se jednou v Root.tsx, a když aplikace nabootovala
        // odhlášená, vrátil ten request 401 a mapa zůstala prázdná. Bez tohohle
        // řádku pak celá session běží na in-code defaultech — u PIP se místo
        // dlaždic Energie ukáže PV2, protože ty Energie existují jen v DB.
        void useLayoutStore.getState().load();
        nav("/", { replace: true });
      } else {
        toast.error(res.message || t.auth.loginFailed);
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) toast.error(t.auth.invalidCredentials);
      else if (status === 423) toast.error(t.auth.accountLocked);
      else if (status === 404) toast.error(t.auth.invalidCredentials);
      else toast.error(t.auth.loginFailed);
    }
  };

  return (
    <Card className="w-full max-w-sm">
      <CardHeader className="items-center text-center">
        <img src="/demirel-icon.webp" alt="Demirel" className="h-12 w-auto mb-2" />
        <CardTitle className="text-2xl">{t.auth.login}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="username">{t.auth.username}</Label>
            <Input
              id="username"
              autoComplete="username"
              autoFocus
              invalid={!!errors.username}
              {...register("username")}
            />
            {errors.username && <p className="text-xs text-destructive">{errors.username.message}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">{t.auth.password}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={!!errors.password}
              {...register("password")}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? t.app.loading : t.auth.login}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
